import { useMutation, useQuery, useQueryClient } from 'react-query';

import { customInstance } from '@ping/api/custom-instance';
import { governanceListParams } from './governance-list-query.utils';
import type { IGovernanceListQuery, IGovernancePaging } from './governance-list-query.utils';

export interface IMandateLimit {
  limitType: string;
  period: string;
  amount: number;
  currency: string;
}

export interface IMandateSummary {
  mechanism: string;
  marketTypes: string[];
  marketIds: string[];
  orderTypes: string[];
  limits: IMandateLimit[];
  effectiveFrom?: string | null;
  effectiveUntil?: string | null;
}

export interface ITradingAuthority {
  isBusinessAdmin: boolean;
  isActiveTrader: boolean;
  mandate?: IMandateSummary | null;
}

export interface ITraderSummary {
  corePassId: string;
  mandate: IMandateSummary;
}

export interface IBusinessAdminSummary {
  corePassId: string;
  isCurrentMember: boolean;
}

export interface ITradingMarket {
  marketId: string;
  marketType: string;
}

export interface ITraderRequestSummary {
  id: string;
  actionType: string;
  status: string;
  phase: string;
  targetCorePassId: string;
  createdAt: string;
  updatedAt: string;
  mandate?: IMandateSummary | null;
}

export interface IMandateChangeRequest {
  targetCorePassId: string;
  marketTypes: string[];
  marketIds: string[];
  orderTypes: string[];
  limits?: IMandateLimit[];
  mechanism: 'Spot';
  effectiveFrom?: string;
  effectiveUntil?: string;
  idempotencyKey: string;
}

export const tradingAuthorityQueryKey = ['business-trading-authority'] as const;
export const businessTradersQueryKey = ['business-traders'] as const;
export const businessAdminsQueryKey = ['business-admins'] as const;
export const tradingMarketsQueryKey = ['business-trading-markets'] as const;
export const traderRequestsQueryKey = ['business-trader-requests'] as const;

const get = <T>(url: string, params?: Record<string, string | number>) =>
  customInstance<T>({ url, method: 'GET', params });

export interface IGovernancePage<T> {
  data: T[];
  paging: IGovernancePaging;
}

export const governanceErrorMessage = (error: unknown) => {
  const data = (
    error as {
      response?: { data?: { error?: string; message?: string; errors?: { message?: string | null }[] } };
    }
  )?.response?.data;
  const detailed = data?.errors
    ?.map(item => item?.message)
    .filter((message): message is string => Boolean(message))
    .join('\n');
  return detailed || data?.message || data?.error || 'The request could not be completed.';
};

export const useTradingAuthority = () =>
  useQuery(tradingAuthorityQueryKey, () => get<ITradingAuthority>('/frontoffice/api/governance/trading/authority'));

export const useBusinessTraders = () =>
  useQuery(businessTradersQueryKey, () => get<ITraderSummary[]>('/frontoffice/api/governance/trading/traders'));

export const useBusinessAdmins = () =>
  useQuery(businessAdminsQueryKey, () => get<IBusinessAdminSummary[]>('/frontoffice/api/governance/trading/admins'));

export const useTradingMarkets = () =>
  useQuery(tradingMarketsQueryKey, () => get<ITradingMarket[]>('/frontoffice/api/governance/trading/markets'));

export const useTraderRequests = (query: IGovernanceListQuery) =>
  useQuery(
    [...traderRequestsQueryKey, query],
    () =>
      get<IGovernancePage<ITraderRequestSummary>>(
        '/frontoffice/api/governance/trading/requests',
        governanceListParams(query)
      ),
    { keepPreviousData: true }
  );

const useGovernanceMutation = <TBody>(url: string) => {
  const queryClient = useQueryClient();
  return useMutation((body: TBody) => customInstance({ url, method: 'POST', data: body }), {
    onSuccess: () => {
      queryClient.invalidateQueries(tradingAuthorityQueryKey);
      queryClient.invalidateQueries(businessTradersQueryKey);
      queryClient.invalidateQueries(businessAdminsQueryKey);
      queryClient.invalidateQueries(traderRequestsQueryKey);
    },
  });
};

export const useAddTrader = () => useGovernanceMutation<IMandateChangeRequest>('/frontoffice/api/governance/traders');

export const useUpdateTradingMandate = () =>
  useGovernanceMutation<IMandateChangeRequest>('/frontoffice/api/governance/traders/mandate');

export const useRemoveTrader = () =>
  useGovernanceMutation<{ targetCorePassId: string; idempotencyKey: string }>(
    '/frontoffice/api/governance/traders/removal'
  );

export const useAddBusinessAdmin = () =>
  useGovernanceMutation<{ targetCorePassId: string; idempotencyKey: string }>(
    '/frontoffice/api/governance/business-admins'
  );
